import { z } from 'zod'

const ssnRegex = /^\d{3}-\d{2}-\d{4}$/
// eslint-disable-next-line security/detect-unsafe-regex -- false positive: optional group with fixed length
const zipRegex = /^\d{5}(-\d{4})?$/
const customerNumRegex = /^[A-Z]{3}\d{7}$/
const feinRegex = /^(?!00)\d{2}-\d{7}$/
const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/
// Years_at_Company__c is a Salesforce number; free text like "20+" is rejected there (CS-8).
const yearsRegex = /^\d{1,2}$|^\d{1,2}\.\d$/
// Frontend validates the display format (with commas); commas are stripped before submission.
// The Go backend moneyPattern (pkg/api/validation.go) validates the raw numeric value.
// eslint-disable-next-line security/detect-unsafe-regex -- false positive: bounded quantifiers with fixed length
const moneyRegex = /^\d{1,3}(,\d{3}){0,2}(\.\d{0,2})?$/

export const VALID_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const

const stateEnum = z.enum(VALID_STATES, { error: 'State is required' })

const baseSchema = z.object({
  applicantName: z.string().min(1, 'Applicant name is required'),
  dob: z.string().min(1, 'Date of birth is required'),
  driverLicense: z.string().min(1, "Driver's license is required"),
  ssn: z.string().regex(ssnRegex, 'SSN must be in format XXX-XX-XXXX'),
  gender: z.string().min(1, 'Gender is required'),
  race: z.string().min(1, 'Race is required'),
  customerNumber: z
    .string()
    .regex(customerNumRegex, 'Must be in format ABC1234567')
    .or(z.literal('')),
  married: z.string().min(1, 'Marital status is required'),
  spouseName: z.string(),
  spouseDOB: z.string(),
  spouseDL: z.string(),
  spouseSSN: z.string(),
  homeAddress1: z.string().min(1, 'Home address is required'),
  homeAddress2: z.string(),
  homeCity: z.string().min(1, 'City is required'),
  homeState: stateEnum,
  homeZip: z.string().regex(zipRegex, 'Zip must be XXXXX or XXXXX-XXXX'),
  companyName: z.string().min(1, 'Company name is required'),
  companyFEIN: z.string().regex(feinRegex, 'FEIN must be in format XX-XXXXXXX').or(z.literal('')),
  businessAddress1: z.string().min(1, 'Business address is required'),
  businessAddress2: z.string(),
  businessCity: z.string().min(1, 'City is required'),
  businessState: stateEnum,
  businessZip: z.string().regex(zipRegex, 'Zip must be XXXXX or XXXXX-XXXX'),
  creditAmount: z
    .string()
    .min(1, 'Credit amount is required')
    .regex(moneyRegex, 'Invalid amount format'),
  personalBank: z.string().min(1, 'Personal bank name is required'),
  businessBank: z.string().min(1, 'Business bank name is required'),
  bankruptcy: z.string().min(1, 'Required'),
  bankruptcyDate: z.string(),
  bankruptcyJurisdiction: z.string(),
  suedForDebt: z.string().min(1, 'Required'),
  grossAnnualSalary: z
    .string()
    .min(1, 'Gross annual salary is required')
    .regex(moneyRegex, 'Invalid amount format'),
  selfEmployed: z.string().min(1, 'Required'),
  employerName: z.string(),
  yearsAtCompany: z
    .string()
    .regex(yearsRegex, 'Enter a number of years, e.g. 20')
    .or(z.literal('')),
  jobTitle: z.string(),
  supervisorName: z.string(),
  supervisorPhone: z
    .string()
    .regex(phoneRegex, 'Phone must be in format (###) ###-####')
    .or(z.literal('')),
  termsAccepted: z.literal(true, { error: 'You must accept the terms and conditions' }),
})

function validateSpouseFields(data: z.infer<typeof baseSchema>, ctx: z.RefinementCtx): void {
  if (data.married !== 'Yes') return
  if (data.spouseSSN && !ssnRegex.test(data.spouseSSN)) {
    ctx.addIssue({
      code: 'custom',
      path: ['spouseSSN'],
      message: 'Spouse SSN must be in format XXX-XX-XXXX',
    })
  }
}

export const applicationSchema = baseSchema.superRefine((data, ctx) => {
  validateSpouseFields(data, ctx)
})

export type ApplicationFormData = z.infer<typeof applicationSchema>

/** Strips display commas from money fields so the backend receives raw numeric strings. */
export function stripMoneyFormatting(data: ApplicationFormData): ApplicationFormData {
  return {
    ...data,
    creditAmount: data.creditAmount.replace(/,/g, ''),
    grossAnnualSalary: data.grossAnnualSalary.replace(/,/g, ''),
  }
}

// termsAccepted intentionally omitted: RHF defaults missing fields to undefined (unchecked),
// requiring the user to explicitly check the box before submission.
// State fields widened to string: defaults start as '' for the placeholder, Zod rejects '' on submit.
type FormDefaults = Omit<ApplicationFormData, 'termsAccepted' | 'homeState' | 'businessState'> & {
  homeState: string
  businessState: string
}

export const DEFAULT_FORM_VALUES: FormDefaults = {
  applicantName: '',
  dob: '',
  driverLicense: '',
  ssn: '',
  gender: '',
  race: '',
  customerNumber: '',
  married: '',
  spouseName: '',
  spouseDOB: '',
  spouseDL: '',
  spouseSSN: '',
  homeAddress1: '',
  homeAddress2: '',
  homeCity: '',
  homeState: '',
  homeZip: '',
  companyName: '',
  companyFEIN: '',
  businessAddress1: '',
  businessAddress2: '',
  businessCity: '',
  businessState: '',
  businessZip: '',
  creditAmount: '',
  personalBank: '',
  businessBank: '',
  bankruptcy: '',
  bankruptcyDate: '',
  bankruptcyJurisdiction: '',
  suedForDebt: '',
  grossAnnualSalary: '',
  selfEmployed: '',
  employerName: '',
  yearsAtCompany: '',
  jobTitle: '',
  supervisorName: '',
  supervisorPhone: '',
}
